import React, { useMemo } from 'react';
import AvatarImage from './AvatarImage';
import UserProfileBadges from './UserProfileBadges';
import { getInitials } from '../hooks/useConsultantBannerData';
import { resolveCoverImageSrc } from '../utils/avatarUrl';
import type { UserProfileBadge } from '../types';

export interface ProfileHeaderProps {
    coverPhotoUrl?: string;
    coverPhotoAlt?: string;
    avatarUrl?: string;
    avatarAlt?: string;
    name: string;
    badges?: UserProfileBadge[];
    handle?: string;
    certification?: string;
    bio?: string;
    className?: string;
    actions?: React.ReactNode;
    onAvatarClick?: () => void;
    avatarEditable?: boolean;
    onCoverClick?: () => void;
    coverEditable?: boolean;
}

function getInitialsFromName(name: string): string {
    return getInitials(name);
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
    coverPhotoUrl,
    coverPhotoAlt = '',
    avatarUrl,
    avatarAlt = '',
    name,
    badges = [],
    handle,
    certification,
    bio,
    className = '',
    actions,
    onAvatarClick,
    avatarEditable = false,
    onCoverClick,
    coverEditable = false,
}) => {
    const initials = useMemo(() => getInitialsFromName(name), [name]);
    const resolvedCoverUrl = useMemo(() => resolveCoverImageSrc(coverPhotoUrl), [coverPhotoUrl]);

    const avatarFallback = (
        <div
            className="flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full border-4 border-white dark:border-slate-900 bg-brand-sage text-white shadow-md"
            aria-label={avatarAlt || name}
        >
            <span className="text-lg md:text-xl font-black">{initials}</span>
        </div>
    );

    const avatarNode = (
        <AvatarImage
            src={avatarUrl}
            alt={avatarAlt || name}
            className="h-20 w-20 md:h-24 md:w-24 rounded-full object-cover object-center border-4 border-white dark:border-slate-900 shadow-md bg-white dark:bg-slate-800"
            fallback={avatarFallback}
        />
    );

    const coverFallback = (
        <div
            className="h-full w-full bg-gradient-to-br from-brand-sage/30 via-sky-100 to-amber-50 dark:from-brand-sage/40 dark:via-slate-800 dark:to-slate-900"
            aria-hidden
        />
    );

    const coverNode = (
        <AvatarImage
            src={resolvedCoverUrl}
            alt={coverPhotoAlt}
            className="h-full w-full object-cover"
            fallback={coverFallback}
        />
    );

    return (
        <header className={`bg-white dark:bg-slate-900 ${className}`}>
            <div className="relative h-28 md:h-32 w-full overflow-hidden">
                {onCoverClick ? (
                    <button
                        type="button"
                        onClick={onCoverClick}
                        className={`block h-full w-full text-start transition-transform active:scale-[0.99] ${
                            coverEditable ? 'ring-2 ring-inset ring-brand-sage/60' : ''
                        }`}
                        aria-label={coverPhotoAlt || name}
                    >
                        {coverNode}
                    </button>
                ) : (
                    coverNode
                )}

                {actions && (
                    <div className="absolute top-3 end-3 z-20 flex items-center gap-2">
                        {actions}
                    </div>
                )}
            </div>

            <div className="relative px-4 md:px-6 pb-5">
                <div className="flex items-end gap-3 -mt-10 md:-mt-12">
                    {onAvatarClick ? (
                        <button
                            type="button"
                            onClick={onAvatarClick}
                            className={`relative z-10 shrink-0 rounded-full transition-transform active:scale-95 ${
                                avatarEditable ? 'ring-2 ring-brand-sage ring-offset-2 ring-offset-white dark:ring-offset-slate-900' : ''
                            }`}
                            aria-label={avatarAlt || name}
                        >
                            {avatarNode}
                        </button>
                    ) : (
                        <div className="relative z-10 shrink-0">{avatarNode}</div>
                    )}

                    <div className="min-w-0 flex-1 pb-1">
                        <div className="flex flex-row items-center gap-2 min-w-0">
                            <h1 className="text-lg md:text-xl font-black text-slate-900 dark:text-slate-100 truncate min-w-0">
                                {name}
                            </h1>
                            <UserProfileBadges badges={badges} />
                        </div>
                        {handle && (
                            <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400 truncate">
                                {handle}
                            </p>
                        )}
                        {certification && (
                            <p className="mt-0.5 text-xs font-bold text-slate-400 dark:text-slate-500 truncate">
                                {certification}
                            </p>
                        )}
                    </div>
                </div>

                {bio?.trim() && (
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                        {bio}
                    </p>
                )}
            </div>
        </header>
    );
};

export default ProfileHeader;
